import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, effect, input, output, signal} from '@angular/core';
import {formatUnits} from 'viem';
import {
    BigintDeliverableDisplayDecimalPipe
} from '../../../../components/bigint-display-decimal-pipe/bigint-deliverable-display-decimal.pipe';
import {ComputedLp} from '../../../../components/dex/dex-streaming/types/computed/computed-lp';
import {ComputedTranche} from '../../../../components/dex/dex-streaming/types/computed/computed-tranche';
import {DisplayDecimalModule} from '../../../../components/display-decimal/index';
import {PartialLoadingIndicatorModule} from '../../../../components/partial-loading-indicator/index';
import {TooltipModule} from '../../../../components/tooltip/index';
import {SparklineModule} from '../../../../mobile/pages/m-quotes-page/m-quotes-mini-chart/sparkline/index';
import {
    DexLpDetailsTableComponent
} from '../../../dex-lp-details-widget/dex-lp-details-table/dex-lp-details-table.component';
import {DexTrancheSparklinePipe} from '../../../dex-tranche-details-widget/dex-tranche-sparkline.pipe';

@Component({
    selector:        'dex-lp-details-standalone',
    templateUrl:     './dex-lp-details-standalone.component.html',
    styleUrls:       ['./dex-lp-details-standalone.component.scss'],
    imports:         [
        BigintDeliverableDisplayDecimalPipe,
        DisplayDecimalModule,
        SparklineModule,
        TooltipModule,
        DexTrancheSparklinePipe,
        AsyncPipe,
        DexLpDetailsTableComponent,
        PartialLoadingIndicatorModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DexLpDetailsStandaloneComponent {

    public readonly lp = input.required<ComputedLp | null>();
    public readonly tranches = input.required<ComputedTranche[]>();
    public readonly isEmbedded = input<boolean>(true);
    public readonly isCollapsed = input<boolean>(false);
    public readonly isExpandCollapseEnabled = input<boolean>(false);
    public readonly isSymbolClickable = input.required<boolean>();

    public readonly toggleCollapsed = output<void>();
    public readonly symbolClick = output<string>();

    // TODO: [LP-SPARKLINE] Debug the shifted last tick in the sparkline (part 1/2)
    public readonly debugSparklineTooltip = signal<string>('');
    private readonly _debugSparklineEffect = effect(() => {
        const lp = this.lp();
        const pos = lp?.$computedPositions()?.[0];
        const pnlNumber = lp?.$uPnlNumber();
        const pnlValueWithDel = pos?.profitOrLoss || {v: undefined, d: undefined};
        const pnlRawStr = pnlValueWithDel?.d
            ? formatUnits(pnlValueWithDel.v || 0n, pnlValueWithDel.d.decimalPlaces)
            : String(pnlValueWithDel?.v);

        let log = (window as any).dexLpPnlLog;
        const lastPoint = (window as any).dexLpPnlLastPoint;

        if (!log) {
            log = [];
            (window as any).dexLpPnlLog = log;
        }

        const logRec = {
            ts: new Date().toISOString(),
            pnlNumber: pnlNumber,
            pnlRawStr,
            lastPoint,
            pos
        };

        log.push(logRec);

        const tooltipStr = [
            logRec.ts,
            `Number(pos.profitOrLoss): ${logRec.pnlNumber}`,
            `formatUnit(pos.profitOrLoss): ${logRec.pnlRawStr}`,
            `chartLastPoint: ${logRec.lastPoint}`
        ].join('\n');

        this.debugSparklineTooltip.set(tooltipStr);
    });

    public onClickToggleCollapsed(event: MouseEvent): void {
        if (this.isExpandCollapseEnabled && !this.getSelectionText()) {
            event.stopPropagation();
            event.preventDefault();
            this.toggleCollapsed.emit();
        }
    }

    public onSymbolClick(symbol: string, event: MouseEvent): void {
        if (this.isSymbolClickable()) {
            event.stopPropagation();
            this.symbolClick.emit(symbol);
        }
    }

    private getSelectionText(): string {
        return window?.getSelection()?.toString() || '';
    }

}
